import type {DiscoveredService} from '../../api/models/discovered-service';
import type {EditScanTargetRequest} from '../../api/models/edit-scan-target-request';
import type {EditTargetScanRequest} from '../../api/models/edit-target-scan-request';
/* eslint-disable */
// Attack Surface (Network Scanner) mock data — all netscan_* alt fallbacks (used until the backend
// read handlers return live data). Two coherent datasets:
//   1. Discovery / Activity read model — discovery-overview, target-overview and history, synthesized
//      from static subnet cards + a scan-history feed.
//   2. Stateful in-memory demo model (`netscanState`) — overview / targets / scanners / scan results,
//      whose create/edit/delete/scan/deploy mutations persist for the session (reset on full reload).

import {MS_DAY} from "../../tools/CommonJS";

const BASE_MS = Date.UTC(2026, 6, 14, 9, 0, 0);
const iso = (daysAgo: number) => new Date(BASE_MS - daysAgo * 86400000).toISOString();

function page<T>(rows: T[], p: any) {
    const from = p?.from ?? 0;
    const len = p?.len || rows.length;
    return {data: rows.slice(from, from + len), rowCount: rows.length};
}

// --- scan history (Attack Surface Activity) --------------------------------------------------
// Past-scan feed for the Activity/History screen. Targets mirror the network-scanner overview
// (HQ IoT VLAN / Lab network / Guest · BYOD) so opening Activity filtered by a clicked target
// shows that target's real runs. Times are now-relative so the "ago"/date display stays fresh.
type Service = DiscoveredService;
type ScanRef = {scanId?: number};
type TargetInput = Partial<EditScanTargetRequest>;
type ScanInput = Partial<EditTargetScanRequest>;
type DeviceQuery = {scanId?: number, targetId?: number, deviceId?: number, statuses?: string[], search?: string,
	deviceType?: string, ip?: string, device?: string, service?: string, port?: string, protocol?: string,
	field?: string, MAC?: string, IP?: string, from?: number, len?: number};
type DeviceRow = {device?: string | null, deviceId?: number, ip?: string | null, status?: string[], scanId?: number,
	services?: Service[], [key: string]: unknown};
type HistoryTarget = {targetId: number; name: string; addresses: string[]};
type HistoryScan = {
    scanId: number; name?: string; targets: HistoryTarget[];
    scanType: "discovery" | "discovery_enrichment"; runId: number; status?: string; scanTime: string; user: string;
};

const dAgo = (days: number) => new Date(Date.now() - days * 86400000).toISOString();

// All targets shown in the Scan Target filter — includes never-scanned targets (e.g. Guest / BYOD),
// so a click on any target resolves to its name even when it has no runs yet.
const historyTargets: HistoryTarget[] = [
    {targetId: 1, name: "HQ IoT VLAN", addresses: ["10.0.10.0/24"]},
    {targetId: 2, name: "Lab network", addresses: ["192.168.10.0/24"]},
    {targetId: 3, name: "Guest / BYOD", addresses: ["10.50.0.0/22"]}
];

// scanId 606 spans two targets (netscan_start-scan accepts targetIds) — exercises the "+N others" display.
// "auto-schedule" rows are recurring scans.
// create.go always assigns a name (user-given, or "Scan #<id>" fallback when none was given) — every scan
// has one; only 606 got a real user-given name here, the rest show the fallback.
const scanHistory: HistoryScan[] = [
    {scanId: 606, name: "Weekly IoT + Guest sweep", targets: [historyTargets[0]], scanType: "discovery_enrichment", runId: 606, status: "success", scanTime: dAgo(2), user: "auto-schedule"},
    {scanId: 605, name: "Scan #605", targets: [historyTargets[0]], scanType: "discovery_enrichment", runId: 605, status: "success", scanTime: dAgo(9), user: "auto-schedule"},
    {scanId: 604, name: "Scan #604", targets: [historyTargets[0]], scanType: "discovery_enrichment", runId: 604, status: "success", scanTime: dAgo(16), user: "auto-schedule"},
    {scanId: 603, name: "Scan #603", targets: [historyTargets[0]], scanType: "discovery_enrichment", runId: 603, status: "success", scanTime: dAgo(23), user: "auto-schedule"},
    {scanId: 602, name: "Scan #602", targets: [historyTargets[0]], scanType: "discovery", runId: 602, status: "success", scanTime: dAgo(30), user: "auto-schedule"},
    {scanId: 601, name: "Scan #601", targets: [historyTargets[0]], scanType: "discovery_enrichment", runId: 601, status: "success", scanTime: dAgo(37), user: "george@acme.com"},
    {scanId: 610, name: "Scan #610", targets: [historyTargets[1]], scanType: "discovery_enrichment", runId: 610, status: "success", scanTime: dAgo(30), user: "auto-schedule"},
    {scanId: 611, name: "Scan #611", targets: [historyTargets[1]], scanType: "discovery", runId: 611, status: "success", scanTime: dAgo(44), user: "j.kim"},
    {scanId: 501, name: "Guest sweep", targets: [historyTargets[2]], scanType: "discovery", runId: 501, status: "success", scanTime: dAgo(5), user: "auto-schedule"},
    {scanId: 502, name: "Weekly HQ + Lab discovery", targets: [historyTargets[0]], scanType: "discovery_enrichment", runId: 502, status: "success", scanTime: dAgo(6), user: "auto-schedule"}
];

// Filter option lists (single source of truth for the Activity filters).
// targetId and scanId mirror the backend's netscan_combi-field shape ({id, value}); the UI maps them to {k, h} (see AttackSurfaceActivity).
export const netscanHistoryTargetOpts = historyTargets.map(t => ({id: t.targetId, value: t.name}));
export const netscanHistoryScanOpts = scanHistory.map(s => ({id: s.scanId, value: s.name ?? `Scan #${s.scanId}`}));
export const netscanHistoryUserOpts = [...new Set(scanHistory.map(s => s.user))].map(user => ({k: user, h: user}));
export const netscanHistoryTypeOpts = [{k: "discovery", h: "Discovery"}, {k: "discovery_enrichment", h: "Enrichment"}];

// Sort values sent by the Activity list (payload `sort.by`) mapped to the string each row orders by —
// "target" has no single field anymore (targets is an array), so it sorts by the first target's name.
const historySortVal: Record<string, (s: HistoryScan) => string> = {
    target: s => s.targets[0]?.name ?? "", scanType: s => s.scanType, lastScan: s => s.scanTime, user: s => s.user
};

function sortHistory(rows: HistoryScan[], sort: any) {
    const val = sort?.by && historySortVal[sort.by];
    if (!val) return [...rows].sort((a, b) => b.scanTime.localeCompare(a.scanTime));   // default: newest first.
    const sorted = [...rows].sort((a, b) => val(a).localeCompare(val(b)));
    return sort.dsc ? sorted.reverse() : sorted;
}

// --- discovery overview / target overview ----------------------------------------------------
export type DiscoverySubnet = {
    targetId: number;
    name: string;
    addresses: string[];                                // CIDRs / IPs / ranges that make up the target.
    ips: number;                                        // scannable addresses across the target's ranges (address-space size, not devices found).
    lastScanAt: string | null;                           // ISO of the last scan (null = never scanned).
    scanType: "discovery" | "discovery_enrichment";     // type of the target's most recent scan.
    devices?: number;                                   // devices discovered (responded) in the last scan — omitted when the scan found nothing.
    new?: number;                                       // first-seen in the last scan.
    unmanaged?: number;                                 // discovered devices with no managed host match.
    lastScanId?: number | null;                         // id of the last scan (null/absent = never scanned) — opens the target at that scan.
    runId?: number;                                      // the last scan's latest run — passed to TargetView so it can query devices immediately.
    scannedSoFar?: number;                              // addresses scanned so far (0 when never scanned; equals totalToScan once complete).
    totalToScan?: number;                               // total addresses planned to be scanned (0 when never scanned).
    status?: "pending" | "running" | "success";   // scan lifecycle — drives the card UI ("success" = completed; absent = legacy).
};

type TargetScanRun = {runId: number; scannedAt: string; devices: number; new: number; latest?: boolean};
type TargetScan = {
    scanId: number; scannedAt: string; scanType: "discovery" | "discovery_enrichment";
    devices: number; new: number; unmanaged: number; runs: TargetScanRun[];
    status: "pending" | "running" | "success";        // scan lifecycle — newest mirrors the discovery card, older scans are completed.
};

// Synthesize one scan's runs: newest mirrors the scan's own totals, older runs (a recurring scan
// re-runs on its schedule) taper off. 1..3 runs, deterministic from scanId.
const buildScanRuns = (scanId: number, scannedAt: string, devices: number, newCount: number): TargetScanRun[] => {
    const count = (scanId % 3) + 1;
    const baseMs = new Date(scannedAt).getTime();
    return Array.from({length: count}, (_, i) => {
        const factor = 1 - i * 0.1;
        return {
            runId: scanId * 10 + (count - i),
            scannedAt: new Date(baseMs - i * 86400000).toISOString(),
            devices: i === 0 ? devices : Math.round(devices * factor),
            new: i === 0 ? newCount : Math.max(0, Math.round(newCount * factor) - i),
            latest: i === 0,
        };
    });
};

// Synthesize a target's scan history from its discovery-overview card: newest scan mirrors the card,
// older scans taper off. Never-scanned subnets return no history.
const buildTargetScans = (subnet: DiscoverySubnet): TargetScan[] => {
    if (!subnet.lastScanAt) return [];
    const count = ((subnet.targetId * 5) % 3) + 1;                               // 1..3 scans.
    const baseMs = new Date(subnet.lastScanAt).getTime();
    return Array.from({length: count}, (_, i) => {
        const scanId = subnet.targetId * 100 + (count - i);                     // newest → highest id.
        const factor = 1 - i * 0.09;
        const scannedAt = new Date(baseMs - i * 7 * 86400000).toISOString();
        // Newest scan inherits the target's live lifecycle (running/pending on the discovery card); prior scans are done.
        const status: TargetScan["status"] = i === 0 ? (subnet.status ?? "success") : "success";
        // A queued (pending) scan hasn't produced results yet — no counts, no runs. Absent counts (nothing found) → 0.
        const queued = status === "pending";
        const devices = queued ? 0 : (i === 0 ? (subnet.devices ?? 0) : Math.round((subnet.devices ?? 0) * factor));
        const newCount = queued ? 0 : (i === 0 ? (subnet.new ?? 0) : Math.max(0, Math.round((subnet.new ?? 0) * factor) - i));
        return {
            scanId,
            scannedAt,
            scanType: subnet.scanType,
            devices,
            new: newCount,
            unmanaged: queued ? 0 : (i === 0 ? (subnet.unmanaged ?? 0) : Math.round((subnet.unmanaged ?? 0) * factor)),
            runs: queued ? [] : buildScanRuns(scanId, scannedAt, devices, newCount),
            status,
        };
    });
};

// --- large synthetic discovered-device pool (demo: 1000+ rows exercising the virtual/infinite-scroll paging) ---
// Deterministic (seeded) so rows stay identical across renders and paging re-slices: the table fetches this pool in
// chunkLen=100 windows via {from, len} (useVirtualPaging), so a request for the same window must return the same rows.
// Attached to scanId 6 (HQ IoT VLAN / target 1) — the target the discovery overview opens by default.
const BULK_SCAN_ID = 6;
const BULK_COUNT = 1200;

const bulkDiscoveredDevices = (() => {
    // mulberry32-style PRNG — pure function of the seed, so every render derives the same row.
    const rand = (seed: number) => {
        let t = (seed + 0x6D2B79F5) >>> 0;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const pick = <T>(arr: T[], r: number): T => arr[Math.min(arr.length - 1, Math.floor(r * arr.length))];
    const hx = (n: number) => (n & 255).toString(16).padStart(2, "0").toUpperCase();

    const typeDefs: {type: string; category: string; os: (string | null)[]; ports: number[]}[] = [
        {type: "Camera", category: "Likely: Camera", os: ["AXIS OS 11.7", "AXIS OS 10.12", "Hikvision 4.1"], ports: [80, 443, 554, 8000]},
        {type: "Display", category: "Likely: Display", os: ["Tizen 6.5", "webOS 6.0", "Android TV 12"], ports: [80, 443, 8001, 9197]},
        {type: "Printer", category: "Likely: Printer", os: ["HP FutureSmart 5", "Canon MEAP", null], ports: [80, 443, 515, 631, 9100]},
        {type: "Server", category: "Likely: Server", os: ["Linux 5.15 (Debian)", "Ubuntu 22.04", "Win Server 2019 Datacenter"], ports: [22, 80, 443, 3306, 5432, 8080]},
        {type: "Workstation", category: "Likely: Workstation", os: ["Win 11 Enterprise", "Win 10 Enterprise", "Ubuntu 24.04"], ports: [135, 139, 445, 3389, 5985]},
        {type: "Macbook", category: "Likely: Workstation", os: ["macOS 14.2.1", "macOS 13.6", null], ports: [22, 445, 548, 5900]},
        {type: "Router", category: "Likely: Router", os: [null, "RouterOS 7.11", "Cisco IOS 15.2"], ports: [22, 23, 53, 80, 161, 443]},
        {type: "Phone", category: "Likely: Phone", os: ["Android 14", "iOS 17.4"], ports: [80, 443, 5060]},
        {type: "IoT", category: "Likely: IoT", os: [null, "FreeRTOS", "Embedded Linux"], ports: [80, 443, 1883, 8883]}
    ];

    return Array.from({length: BULK_COUNT}, (_, k) => {
        const i = k + 1;
        const td = pick(typeDefs, rand(i * 7));
        const os = pick(td.os, rand(i * 11 + 3));
        const oct3 = Math.floor(k / 254), oct4 = (k % 254) + 1;             // roll octets so every IP stays inside the target's /20.
        const hasMac = rand(i * 13 + 5) > 0.08;                             // a few respond IP-only (no MAC → low confidence).
        const managed = rand(i * 17 + 2) < 0.10;                            // ~10% already carry a Remedio sensor.
        const isNew = !managed && rand(i * 19 + 4) < 0.14;                  // ~14% of unmanaged were first seen in this scan.
        const nPorts = Math.floor(rand(i * 23 + 6) * (td.ports.length + 1));
        const named = rand(i * 29 + 8) < 0.45;
        return {
            deviceId: 1000 + k,
            ip: `10.10.${oct3}.${oct4}`,
            mac: hasMac ? ["AC", "DE", "48", hx(i * 7), hx(i * 13), hx(i * 29)].join(":") : null,
            device: managed ? `HOST-${1000 + k}` : named ? `${td.type.toLowerCase()}-${oct3}-${oct4}` : "unknown",
            os,
            status: managed ? "managed" : "unmanaged",
            isNew,
            deviceType: td.type,
            category: managed ? undefined : td.category,
            lowConfidence: !hasMac,
            managedDeviceId: managed ? 1 : undefined,
            scanId: BULK_SCAN_ID,
            firstSeen: dAgo(isNew ? 2 : 10 + Math.floor(rand(i * 31 + 9) * 200)),
            lastSeen: dAgo(2),
            services: [...td.ports].sort((a, b) => a - b).slice(0, nPorts)
                .map((p): {port: number; protocol: string; service?: string; version?: string} => ({port: p, protocol: "tcp"}))
        };
    });
})();

// Curated scanId-6 rows below (deviceIds 201–212): 12 devices, of which 3 are new and 11 unmanaged — folded into the KPIs.
const bulkStats = {
    total: bulkDiscoveredDevices.length + 12,
    new: bulkDiscoveredDevices.filter(d => d.isNew && d.status === "unmanaged").length + 3,
    unmanaged: bulkDiscoveredDevices.filter(d => d.status === "unmanaged").length + 11
};

// The subnet cards without lastScanId — filled in below from each target's synthesized scan history.
const discoverySubnetsBase: Omit<DiscoverySubnet, "lastScanId">[] = [
    // Completed scans: status "success" → show the last-scan results (stat boxes).
    {targetId: 1, name: "TLV HQ - IoT", addresses: ["10.10.0.0/20"], ips: 4094,
        lastScanAt: "2026-07-09T09:00:00.000Z", scanType: "discovery", status: "success",
        devices: bulkStats.total, new: bulkStats.new, unmanaged: bulkStats.unmanaged},
    // Deliberately long name to exercise title ellipsis on the card and Target view header.
    {targetId: 2, name: "Remedio.io TLV HQ - Corporate Enterprise Environment General Purpose Subnet", addresses: ["10.10.1.0/24"], ips: 254,
        lastScanAt: iso(1), scanType: "discovery_enrichment", status: "success",
        devices: 118, new: 3, unmanaged: 22},
    {targetId: 3, name: "Data Center", addresses: ["10.20.0.0/23"], ips: 510,
        lastScanAt: iso(2), scanType: "discovery_enrichment", status: "success",
        devices: 86, new: 0, unmanaged: 9},
    {targetId: 4, name: "Guest / BYOD", addresses: ["10.50.0.0/22"], ips: 1022,
        lastScanAt: iso(4), scanType: "discovery", status: "success",
        devices: 41, new: 12, unmanaged: 41},
    // Completed scan ("success") that found nothing → no device counts in the response; the stat boxes render zeros.
    {targetId: 9, name: "Isolated OT segment", addresses: ["10.60.0.0/24"], ips: 254,
        lastScanAt: iso(1), scanType: "discovery", status: "success", scannedSoFar: 254, totalToScan: 254},
    // Never scanned → scannedSoFar/totalToScan 0, no status, no counts → "Not scanned yet".
    {targetId: 5, name: "Lab network", addresses: ["192.168.10.0/24"], ips: 254,
        lastScanAt: null, scanType: "discovery", scannedSoFar: 0, totalToScan: 0},
    // Running: status "running" + scannedSoFar/totalToScan drive the progress bar (blocks "Scan now").
    {targetId: 6, name: "AWS prod VPC - DB subnets", addresses: ["172.31.2.0/24"], ips: 254,
        lastScanAt: iso(4), scanType: "discovery_enrichment", status: "running",
        devices: 0, new: 0, unmanaged: 0, scannedSoFar: 128, totalToScan: 254},
    // Pending: status "pending" → empty (0%) progress bar with a "queued" tooltip (blocks "Scan now").
    {targetId: 7, name: "DMZ segment", addresses: ["10.30.0.0/24"], ips: 254,
        lastScanAt: null, scanType: "discovery_enrichment", status: "pending",
        devices: 0, new: 0, unmanaged: 0},
    // Pending re-scan of an already-scanned target: has completed history + a freshly queued scan, so its
    // Target view shows a "Pending" pill on the newest scan (blocks "Scan now") above the completed runs.
    {targetId: 8, name: "Branch office - Berlin", addresses: ["10.40.0.0/24"], ips: 254,
        lastScanAt: iso(3), scanType: "discovery_enrichment", status: "pending",
        devices: 64, new: 5, unmanaged: 12}
];

// --- World-A discovered devices (TargetView table + device drilldown) -------------------------
// The discovery / target-overview read model is the single source of truth. Each target's discovered-device
// pool is synthesized from the SAME subnet + scan history that drives its card and header KPIs, so opening
// any discovery card lands on a devices table whose totals, statuses and addresses line up exactly with the
// overview (live hosts / new / unmanaged) — no second, unrelated dataset. Deterministic (seeded by scanId)
// so the virtual-paging table re-slices the same rows, and each row's id encodes its scan so the device
// drilldown can regenerate the exact same device.

// mulberry32 PRNG — pure function of the seed.
const rngA = (seed: number) => {
    let t = (seed + 0x6D2B79F5) >>> 0;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
const pickA = <T>(arr: T[], r: number): T => arr[Math.min(arr.length - 1, Math.floor(r * arr.length))];
const hexA = (n: number) => (n & 255).toString(16).padStart(2, "0").toUpperCase();

// IPv4 <-> int helpers, so every synthesized device lands inside its target's CIDR.
const ipToIntA = (ip: string) => ip.split(".").reduce((a, o) => ((a << 8) + (Number(o) & 255)) >>> 0, 0) >>> 0;
const intToIpA = (n: number) => [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join(".");
const subnetOfIpA = (ip: string) => ip.replace(/\.\d+$/, ".0/24");

const deviceTypeDefsA: {type: string; category: string; os: (string | null)[]; ports: number[]}[] = [
    {type: "Camera", category: "Likely: Camera", os: ["AXIS OS 11.7", "AXIS OS 10.12", "Hikvision 4.1"], ports: [80, 443, 554, 8000]},
    {type: "Display", category: "Likely: Display", os: ["Tizen 6.5", "webOS 6.0", "Android TV 12"], ports: [80, 443, 8001, 9197]},
    {type: "Printer", category: "Likely: Printer", os: ["HP FutureSmart 5", "Canon MEAP", null], ports: [80, 443, 515, 631, 9100]},
    {type: "Server", category: "Likely: Server", os: ["Linux 5.15 (Debian)", "Ubuntu 22.04", "Win Server 2019 Datacenter"], ports: [22, 80, 443, 3306, 5432, 8080]},
    {type: "Workstation", category: "Likely: Workstation", os: ["Win 11 Enterprise", "Win 10 Enterprise", "Ubuntu 24.04"], ports: [135, 139, 445, 3389, 5985]},
    {type: "Macbook", category: "Likely: Workstation", os: ["macOS 14.2.1", "macOS 13.6", null], ports: [22, 445, 548, 5900]},
    {type: "Router", category: "Likely: Router", os: [null, "RouterOS 7.11", "Cisco IOS 15.2"], ports: [22, 23, 53, 80, 161, 443]},
    {type: "Phone", category: "Likely: Phone", os: ["Android 14", "iOS 17.4"], ports: [80, 443, 5060]},
    {type: "IoT", category: "Likely: IoT", os: [null, "FreeRTOS", "Embedded Linux"], ports: [80, 443, 1883, 8883]}
];

// Common ports get a service name even when the scan didn't enrich them; genuinely uncommon ports stay bare.
const wellKnownPortsA: Record<number, string> = {
    22: "SSH", 23: "Telnet", 53: "DNS", 80: "HTTP", 135: "MS-RPC", 139: "NetBIOS", 161: "SNMP", 443: "HTTPS",
    445: "SMB", 515: "LPD", 554: "RTSP", 631: "IPP", 1883: "MQTT", 3306: "MySQL", 3389: "RDP", 5060: "SIP",
    5432: "PostgreSQL", 5900: "VNC", 5985: "WinRM", 8000: "HTTP-alt", 8001: "HTTP-alt", 8080: "HTTP-alt",
    8443: "HTTPS-alt", 8883: "MQTTS", 9100: "JetDirect", 9197: "DIAL"
};
const labelServicesA = (services: {port: number; protocol: string; service?: string; version?: string}[]) =>
    services.map(s => s.service || !wellKnownPortsA[s.port] ? s : {...s, service: wellKnownPortsA[s.port]});
const svcNamesA = (services: {service?: string; version?: string}[] = []) =>
    [...new Set(services.filter(s => s.service).map(s => [s.service, s.version].filter(Boolean).join(" ")))];
// Per-port exposure for the device drilldown: one row per protocol/port, sorted by port.
const svcExposureA = (services: {port: number; protocol: string; service?: string; version?: string}[] = []) => {
    const byPort = new Map<string, any>();
    services.forEach(s => {
        const key = `${s.protocol}/${s.port}`, cur = byPort.get(key);
        if (!cur || (s.service && !cur.service))
            byPort.set(key, {port: s.port, protocol: s.protocol, service: s.service || "", ...(s.version && {version: s.version})});
    });
    return [...byPort.values()].sort((a, b) => a.port - b.port);
};

// Resolve a discovery subnet + one of its synthesized scans from (scanId, targetId). World-A scanIds encode
// their target (targetId * 100 + n), so a scanId alone resolves the target; when targetId is given it wins on
// mismatch (e.g. an Activity row whose scanId belongs elsewhere), keeping the header and table on the same target.
const scanOfIdA = (scanId?: number, targetId?: number): {subnet: DiscoverySubnet; scan?: TargetScan} | undefined => {
    const tid = targetId != null ? Number(targetId) : undefined;
    const subnetOf = (t: number) => {
        const base = discoverySubnetsBase.find(x => x.targetId === t);
        return base ? {...base, lastScanId: null} as DiscoverySubnet : undefined;
    };
    const bySid = (() => {
        if (scanId == null) return undefined;
        const subnet = subnetOf(Math.floor(Number(scanId) / 100));
        if (!subnet) return undefined;
        const scan = buildTargetScans(subnet).find(s => s.scanId === Number(scanId));
        return scan ? {subnet, scan} : undefined;
    })();
    if (bySid && (tid == null || bySid.subnet.targetId === tid)) return bySid;
    if (tid != null) {
        const subnet = subnetOf(tid);
        if (subnet) return {subnet, scan: buildTargetScans(subnet)[0]};
    }
    return bySid;
};

// A target's discovered-device pool for one scan, sized to that scan's KPIs: exactly `scan.devices` rows,
// `scan.unmanaged` of them unmanaged (of which `scan.new` are new), the rest managed — scattered across the
// target's address space. Queued (pending) scans have no results yet.
const devicesForScanA = (subnet: DiscoverySubnet, scan?: TargetScan): any[] => {
    if (!scan || scan.status === "pending") return [];
    const total = scan.devices;
    const unmanagedCount = Math.min(total, scan.unmanaged);
    const newCount = Math.min(unmanagedCount, scan.new);
    const base = ipToIntA((subnet.addresses[0] || "10.0.0.0/24").split("/")[0]);
    const lastSeen = scan.scannedAt;
    const lastMs = new Date(scan.scannedAt).getTime();
    // Scatter the status buckets across the pool (exact counts) so new / unmanaged / managed interleave by IP.
    const rank = new Array<number>(total);
    Array.from({length: total}, (_, i) => i)
        .sort((a, b) => rngA(scan.scanId * 911 + a) - rngA(scan.scanId * 911 + b))
        .forEach((idx, r) => rank[idx] = r);
    return Array.from({length: total}, (_, i) => {
        const seed = scan.scanId * 100003 + i + 1;
        const isNew = rank[i] < newCount;
        const managed = rank[i] >= unmanagedCount;
        const td = pickA(deviceTypeDefsA, rngA(seed * 7));
        const os = pickA(td.os, rngA(seed * 11 + 3));
        const hasMac = rngA(seed * 13 + 5) > 0.06;                          // a few respond IP-only (no MAC → low confidence).
        const named = rngA(seed * 29 + 8) < 0.5;
        const nPorts = Math.floor(rngA(seed * 23 + 6) * (td.ports.length + 1));
        const lastOctet = (base + i + 1) & 255;
        const services = labelServicesA([...td.ports].sort((a, b) => a - b).slice(0, nPorts).map(p => ({port: p, protocol: "tcp"})));
        const firstMs = isNew ? lastMs : lastMs - (20 + Math.floor(rngA(seed * 31 + 9) * 260)) * 86400000;
        return {
            deviceId: scan.scanId * 100000 + i,                             // encodes scanId + index → the drilldown regenerates this exact device.
            ip: intToIpA((base + i + 1) >>> 0),
            mac: hasMac ? ["AC", "DE", "48", hexA(seed * 7), hexA(seed * 13), hexA(seed * 29)].join(":") : null,
            device: managed ? `HOST-${scan.scanId}-${i}` : named ? `${td.type.toLowerCase()}-${lastOctet}` : "unknown",
            os,
            status: managed ? ["managed"] : isNew ? ["new", "unmanaged"] : ["unmanaged"],
            isNew,
            deviceType: td.type,
            category: managed ? undefined : td.category,
            lowConfidence: !hasMac,
            managedDeviceId: managed ? 1 : undefined,
            scanId: scan.scanId,
            firstScanId: isNew ? scan.scanId : subnet.targetId * 100 + 1,
            lastScanId: scan.scanId,
            firstSeen: new Date(firstMs).toISOString(),
            lastSeen,
            services,
        };
    });
};

// Cache each scan's pool so paging re-slices (and the drilldown) hit identical rows without regenerating.
const devicePoolCacheA = new Map<number, any[]>();
const poolForA = (resolved?: {subnet: DiscoverySubnet; scan?: TargetScan}): any[] => {
    if (!resolved?.scan) return [];
    const key = resolved.scan.scanId;
    if (!devicePoolCacheA.has(key)) devicePoolCacheA.set(key, devicesForScanA(resolved.subnet, resolved.scan));
    return devicePoolCacheA.get(key)!;
};

const filterDevicesA = (data: any[], {flags, statuses, search, deviceType, ip, device, deviceId, service, port, protocol}: any = {}) => {
    let all = data;
    // The Status filter sends its buckets as `flags` (UI) — fall back to `statuses` (backend name). Empty = all.
    const st = flags?.length ? flags : statuses?.length ? statuses : undefined;
    if (st) all = all.filter(d => st.some((s: string) => d.status?.includes(s)));
    if (deviceType) all = all.filter(d => d.deviceType === deviceType);
    if (ip?.trim()) { const q = ip.trim().toLowerCase(); all = all.filter(d => d.ip?.toLowerCase().includes(q)); }
    if (deviceId != null) all = all.filter(d => String(d.deviceId) === String(deviceId));
    else if (device?.trim()) { const q = device.trim().toLowerCase(); all = all.filter(d => d.device?.toLowerCase().includes(q)); }
    if (service?.trim()) { const q = service.trim().toLowerCase(); all = all.filter(d => d.services?.some((s: any) => s.service?.toLowerCase().includes(q))); }
    if (port?.trim()) { const q = port.trim(); all = all.filter(d => d.services?.some((s: any) => String(s.port) === q)); }
    if (protocol?.trim()) { const q = protocol.trim().toLowerCase(); all = all.filter(d => d.services?.some((s: any) => s.protocol?.toLowerCase() === q)); }
    if (search?.trim()) {
        const q = search.trim().toLowerCase();
        all = all.filter(d => [d.ip, d.device, d.mac, d.category, d.os, d.deviceType, ...svcNamesA(d.services)].some(v => v && String(v).toLowerCase().includes(q)));
    }
    return all;
};

// netscan_target-discovered-devices — served from the discovery read model so counts / statuses / addresses
// match the target-overview header. Paged via {from, len} for the virtual/infinite-scroll table.
const scanResultsA = ({scanId, targetId, flags, statuses, search, deviceType, ip, device, deviceId, service, port, protocol, from = 0, len}: any = {}) => {
    const resolved = scanOfIdA(scanId, targetId);
    const pool = poolForA(resolved);
    const filtered = filterDevicesA(pool, {flags, statuses, search, deviceType, ip, device, deviceId, service, port, protocol});
    const scan = resolved?.scan;
    return {
        data: filtered.slice(from, len == null ? undefined : from + len),
        rowCount: filtered.length,
        totalInScan: pool.length,
        scan: scan && {
            scanId: scan.scanId, targetId: resolved!.subnet.targetId, name: resolved!.subnet.name,
            scanType: scan.scanType, startedAt: scan.scannedAt, liveHosts: scan.devices,
            state: scan.status, range: resolved!.subnet.addresses[0],
        },
    };
};

// netscan_unmanaged-device — the discovered-device drilldown. deviceId encodes (scanId, index), so it
// regenerates the exact row the table showed; MAC / IP within the scan are accepted as fallbacks. runId pins
// the run whose services to return (the demo model's pool is seeded per scan, so runId is accepted but not varied).
const unmanagedDeviceA = ({deviceId, runId: _runId, scanId, MAC, IP}: any = {}) => {
    let sid = scanId != null ? Number(scanId) : undefined;
    let idx: number | undefined;
    if (deviceId != null && Number(deviceId) >= 100000) {
        sid = Math.floor(Number(deviceId) / 100000);
        idx = Number(deviceId) % 100000;
    }
    const resolved = scanOfIdA(sid);
    const pool = poolForA(resolved);
    const d = (idx != null && pool[idx])
        || (MAC && pool.find(x => x.mac && x.mac.toLowerCase() === String(MAC).toLowerCase()))
        || (IP && pool.find(x => x.ip === IP))
        || undefined;
    if (!d) return null;
    return {
        deviceId: d.deviceId, ip: d.ip, mac: d.mac, device: d.device, category: d.category,
        firstDiscovered: d.firstSeen, lastSeen: d.lastSeen,
        discoveredBy: "WIN11-OPS-15", subnet: subnetOfIpA(d.ip),
        scanType: resolved?.scan?.scanType ?? "discovery", exposure: svcExposureA(d.services),
    };
};

// --- stateful in-memory demo model -----------------------------------------------------------
// Mutations (create/edit/delete/scan/deploy) persist for the session so the dashboard reflects them
// on refetch without a backend. Resets on full reload. alt() funcs receive {api, ...params}. The
// deploy picker and deploy resolution share one device pool, so the whole flow has full fidelity.
const netscanState = (() => {
    const daysAgo = (d: number) => new Date(Date.now() - d * MS_DAY).toISOString();
    const subnetOf = (ip?: string) => ip ? ip.replace(/\.\d+$/, ".0/24") : undefined;
    let nextId = 7;
    type Target = {
        targetId: number, name: string, description: string, addresses: string[],
        exclusions: string[], scannerIds: number[], lastScan: string | null, devices: number | null,
    };
    let targets: Target[] = [
        {targetId: 1, name: "HQ IoT VLAN", description: "Production IoT segment", addresses: ["10.0.10.0/24"], exclusions: [], scannerIds: [1], lastScan: daysAgo(2), devices: 41},
        {targetId: 2, name: "Lab network", description: "", addresses: ["192.168.10.0/24"], exclusions: [], scannerIds: [2], lastScan: daysAgo(30), devices: 23},
        {targetId: 3, name: "Guest / BYOD", description: "", addresses: ["10.50.0.0/22"], exclusions: [], scannerIds: [], lastScan: null, devices: null},
        // Enterprise-scale ranges — millions of addresses. Selecting these against a bounded maintenance window trips the "estimate exceeds window" guard.
        {targetId: 4, name: "Corp WAN (10.0.0.0/8)", description: "Enterprise-wide /8 discovery", addresses: ["10.0.0.0/8"], exclusions: [], scannerIds: [1, 2, 3, 4, 5], lastScan: null, devices: null},
        {targetId: 5, name: "EMEA supernet (172.16.0.0/12)", description: "EMEA regional supernet", addresses: ["172.16.0.0/12"], exclusions: [], scannerIds: [3, 4], lastScan: daysAgo(20), devices: 2140},
        {targetId: 6, name: "APAC campus (10.20.0.0/14)", description: "APAC campus range", addresses: ["10.20.0.0/14"], exclusions: [], scannerIds: [4, 5], lastScan: daysAgo(12), devices: 980}
    ];
    type Scanner = {device: string, deviceId: number, ip: string, subnet: string | undefined, lastScan: string | null, online: boolean};
    let scanners: Scanner[] = [
        {device: "WIN11-OPS-15", deviceId: 1, ip: "10.0.10.18", subnet: "10.0.10.0/24", lastScan: daysAgo(26), online: true},
        {device: "WIN10-LAB-02", deviceId: 2, ip: "192.168.10.12", subnet: "192.168.10.0/24", lastScan: daysAgo(39), online: true},
        {device: "DC-SCAN-03", deviceId: 3, ip: "10.0.0.30", subnet: "10.0.0.0/24", lastScan: daysAgo(1), online: true},
        {device: "EMEA-SCAN-04", deviceId: 4, ip: "172.16.0.40", subnet: "172.16.0.0/24", lastScan: daysAgo(1), online: true},
        {device: "APAC-SCAN-05", deviceId: 5, ip: "10.20.0.50", subnet: "10.20.0.0/24", lastScan: daysAgo(3), online: false}
    ];
    // Managed devices eligible to become scanners — the deploy picker's source. Already-deployed scanners stay in the
    // list flagged `isScanner`; `ip` is kept for internal deploy bookkeeping (subnet) and is not part of the response.
    const deployablePool = [
        {deviceId: 5001, device: "WIN11-FIN-04", os: "Win 11 Enterprise", clientVersion: "2.34.7.0", ip: "10.0.5.21", lastScan: daysAgo(0.04)},
        {deviceId: 5002, device: "LON-SCAN-01", os: "Win 10 Enterprise", clientVersion: "2.34.7.0", ip: "10.0.5.88", lastScan: daysAgo(0.05)},
        {deviceId: 5003, device: "UBUNTU-SCAN", os: "Ubuntu 24.04", clientVersion: "2.34.7.0", ip: "10.0.5.90", lastScan: daysAgo(0.06)},
        {deviceId: 5004, device: "REDHAT-SAAS", os: "Redhat 9.0", clientVersion: "2.34.7.0", ip: "192.168.0.120", lastScan: daysAgo(0.08)},
        {deviceId: 5005, device: "SERVER-2016", os: "Win Server 2016 Standard", clientVersion: "2.34.7.0", ip: "192.168.0.113", lastScan: daysAgo(0.1)},
        {deviceId: 5006, device: "WIN10-OPS-22", os: "Win 10 Enterprise", clientVersion: "2.34.7.0", ip: "192.168.0.105", lastScan: daysAgo(0.12)},
        {deviceId: 5007, device: "MAC-DESIGN-08", os: "macOS 14.2.1", clientVersion: "2.34.7.0", ip: "192.168.10.5", lastScan: daysAgo(0.2)},
        {deviceId: 5008, device: "GYTPOLQA-DC", os: "Win Server 2019 Datacenter", clientVersion: "2.34.7.0", ip: "192.168.0.100", lastScan: daysAgo(0.3)}
    ];
    // netscan_overview's upcoming list only ever contains recurring scans (applyScheme afterTime/
    // nextWeekDay) — there's no one-time future schedule. Each scan carries its own name.
    let upcoming: any[] = [
        {
            scanId: 501, name: "Guest sweep", targets: [{targetId: 3, name: "Guest / BYOD", addresses: ["10.50.0.0/22"]}],
            scannerIds: [], scanType: "discovery",
            applyScheme: "afterTime", afterTime: 60, timezone: 180, window: 120,
        },
        {
            scanId: 502, name: "Weekly HQ + Lab discovery",
            targets: [{targetId: 1, name: "HQ IoT VLAN", addresses: ["10.0.10.0/24"]}, {targetId: 2, name: "Lab network", addresses: ["192.168.10.0/24"]}],
            scannerIds: [1], scanType: "discovery_enrichment",
            applyScheme: "nextWeekDay", weekDay: "Monday", afterTime: 120, timezone: 180, window: 180,
            deactivatedAt: daysAgo(3), deactivatedBy: {user: "george@acme.com", userName: "George Mann", title: null},
        }
    ];
    let nextScanId = 7;
    const mkScan = (scanId: number, days: number, deviceCount: number, newCount: number | null, scanType = "discovery_enrichment") => ({
        scanId, targetId: 1, name: "HQ IoT VLAN", scanType, startedAt: daysAgo(days), deviceCount, newCount,
        scannerId: 1, state: "completed"
    });
    let scans = [
        mkScan(6, 2, 41, 3), mkScan(5, 9, 40, 1), mkScan(4, 16, 39, 2), mkScan(3, 23, 38, 0),
        mkScan(2, 30, 35, null, "discovery"), mkScan(1, 37, 31, 5),
        {scanId: 10, targetId: 2, name: "Lab network", scanType: "discovery_enrichment", startedAt: daysAgo(30), deviceCount: 23, newCount: 2, scannerId: 2, state: "completed"}
    ];
    // Services is an array of open ports; service/version are present only where exposure scanning identified the port.
    const svc = (port: number, protocol: string, service?: string, version?: string) =>
        ({port, protocol, ...(service && {service}), ...(version && {version})});
    const svcLabel = (s: Service) => [s.service, s.version].filter(Boolean).join(" ");
    const svcNames = (services?: Service[]) => [...new Set((services ?? []).filter(s => s.service).map(svcLabel))];
    // Per-port exposure for the device-info drilldown: dedupe by protocol/port, preferring the identified (named) entry.
    // Service name and version are separate fields (version omitted when the scan didn't identify it).
    const svcExposure = (services?: Service[]) => {
        const byPort = new Map();
        (services ?? []).forEach(s => {
            const key = `${s.protocol}/${s.port}`, cur = byPort.get(key);
            if (!cur || (s.service && !cur.service))
                byPort.set(key, {port: s.port, protocol: s.protocol, service: s.service || "", ...(s.version && {version: s.version})});
        });
        return [...byPort.values()].sort((a, b) => a.port - b.port);
    };
    // Common ports get a service name even when the scan didn't enrich them; genuinely uncommon ports (e.g. 17988) stay bare.
    const wellKnownPorts = {
        20: "FTP", 21: "FTP", 22: "SSH", 23: "Telnet", 25: "SMTP", 53: "DNS", 67: "DHCP", 68: "DHCP", 69: "TFTP",
        80: "HTTP", 110: "POP3", 111: "RPC", 123: "NTP", 135: "MS-RPC", 137: "NetBIOS", 138: "NetBIOS", 139: "NetBIOS",
        143: "IMAP", 161: "SNMP", 162: "SNMP", 389: "LDAP", 443: "HTTPS", 445: "SMB", 465: "SMTPS", 514: "Syslog",
        587: "SMTP", 631: "IPP", 636: "LDAPS", 993: "IMAPS", 995: "POP3S", 1433: "MS-SQL", 1521: "Oracle", 1723: "PPTP",
        2049: "NFS", 3306: "MySQL", 3389: "RDP", 5060: "SIP", 5432: "PostgreSQL", 5900: "VNC", 5985: "WinRM",
        6379: "Redis", 8080: "HTTP", 8443: "HTTPS", 9100: "JetDirect", 27017: "MongoDB"
    };
    // firstScanId/lastScanId are derived per row so the mock stays self-consistent with each device's services array.
    const discoveredDevices = [
        {deviceId: 201, ip: "10.0.10.55", mac: "AC:DE:48:00:11:22", device: "unknown", os: "AXIS OS 11.7", status: "unmanaged", isNew: false,
            deviceType: "Camera", category: "Likely: Camera", lowConfidence: false, scanId: 6, firstSeen: daysAgo(120), lastSeen: daysAgo(2),
            services: [svc(80, "tcp", "Boa HTTPd", "0.94"), svc(443, "tcp", "lighttpd", "1.4.50"), svc(554, "tcp", "RTSP")]},
        {deviceId: 202, ip: "10.0.10.56", mac: "AC:DE:48:00:11:30", device: "unknown", os: "AXIS OS 10.12", status: "unmanaged", isNew: false,
            deviceType: "Camera", category: "Likely: Camera", scanId: 6, firstSeen: daysAgo(120), lastSeen: daysAgo(2),
            services: [svc(80, "tcp", "Boa HTTPd", "0.94"), svc(554, "tcp", "RTSP")]},
        {deviceId: 203, ip: "10.0.10.20", mac: "78:CA:39:1E:5C:00", device: "samsung-conf-tv", os: "Tizen 6.5", status: "unmanaged", isNew: true,
            deviceType: "Display", category: "Likely: Display", scanId: 6, firstSeen: daysAgo(60), lastSeen: daysAgo(2),
            services: [svc(80, "tcp"), svc(443, "tcp", "Samsung AllShare")]},
        {deviceId: 204, ip: "10.0.10.21", mac: "78:CA:39:1E:5C:11", device: "samsung-lobby-tv", os: "Tizen 6.5", status: "unmanaged", isNew: true,
            deviceType: "Display", category: "Likely: Display", scanId: 6, firstSeen: daysAgo(60), lastSeen: daysAgo(2),
            services: [svc(80, "tcp"), svc(443, "tcp")]},
        {deviceId: 205, ip: "10.0.10.99", mac: "00:27:E8:43:1F:21", device: "unknown", os: "Linux 5.15 (Debian)", status: "unmanaged", isNew: false,
            deviceType: "Server", category: "Likely: Server", lowConfidence: true, scanId: 6, firstSeen: daysAgo(45), lastSeen: daysAgo(2),
            services: [
                svc(21, "tcp"), svc(21, "tcp", "vsFTPd", "3.0.2"), svc(22, "tcp"), svc(22, "tcp", "OpenSSH", "7.7"),
                svc(80, "tcp", "nginx", "1.18.0"), svc(443, "tcp"), svc(53, "udp")
            ]},
        {deviceId: 206, ip: "10.0.10.18", mac: "AA:BB:CC:DD:EE:99", device: "WIN11-OPS-15", os: "Win 11 Enterprise", status: "managed", isNew: false,
            deviceType: "Workstation", managedDeviceId: 1, scanId: 6, firstSeen: daysAgo(200), lastSeen: daysAgo(2),
            services: [svc(445, "tcp", "microsoft-ds"), svc(3389, "tcp", "Windows Remote Desktop Protocol")]},
        {deviceId: 207, ip: "10.0.10.77", mac: null, device: "unknown", os: null, status: "unmanaged", isNew: false,
            deviceType: "IoT", category: "Likely: IoT", lowConfidence: true, scanId: 6, firstSeen: daysAgo(14), lastSeen: daysAgo(2), services: []},
        {deviceId: 208, ip: "10.0.10.88", mac: null, device: null, os: null, status: "unmanaged", isNew: true,
            deviceType: "IoT", category: "Likely: IoT", lowConfidence: true, scanId: 6, firstSeen: daysAgo(14), lastSeen: daysAgo(2), services: []},
        {deviceId: 209, ip: "10.0.10.1", mac: "00:50:56:AE:13:90", device: "unknown", os: null, status: "unmanaged", isNew: false,
            deviceType: "Router", category: "Likely: Router", scanId: 6, firstSeen: daysAgo(120), lastSeen: daysAgo(2),
            services: [svc(443, "tcp", "PHP")]},
        {deviceId: 210, ip: "10.0.10.41", mac: "B0:60:88:47:A7:0C", device: "ELIOR-3D123D3", os: "Windows NT 10.0 (build 26100)", status: "unmanaged", isNew: false,
            deviceType: "PC", category: "Likely: Workstation", scanId: 6, firstSeen: daysAgo(30), lastSeen: daysAgo(2),
            services: [svc(139, "tcp"), svc(445, "tcp"), svc(3389, "tcp", "Windows Remote Desktop Protocol")]},
        {deviceId: 211, ip: "10.0.10.123", mac: "50:F2:65:EC:A7:31", device: "Dekels-MacBook-Pro.local", os: null, status: "unmanaged", isNew: false,
            deviceType: "Macbook", category: "Likely: Workstation", scanId: 6, firstSeen: daysAgo(80), lastSeen: daysAgo(2),
            services: [svc(22, "tcp"), svc(445, "tcp"), svc(5900, "tcp", "Apple Remote Desktop"), svc(53, "udp")]},
        {deviceId: 212, ip: "10.0.10.134", mac: "7C:A6:2A:88:8A:0C", device: "ip-10-0-10-134", os: null, status: "unmanaged", isNew: false,
            deviceType: "iLO 6 in ProLiant DL360 Gen11", category: "Likely: Server", scanId: 6, firstSeen: daysAgo(90), lastSeen: daysAgo(2),
            services: [svc(22, "tcp", "OpenSSH", "8.0"), svc(443, "tcp"), svc(17988, "tcp"), svc(53, "udp")]},
        {deviceId: 213, ip: "192.168.10.55", mac: "AA:BB:CC:DD:EE:04", device: "unknown", os: null, status: "unmanaged", isNew: true,
            deviceType: "Camera", category: "Likely: Camera", scanId: 10, firstSeen: daysAgo(7), lastSeen: daysAgo(1),
            services: [svc(554, "tcp", "RTSP")]},
        {deviceId: 214, ip: "192.168.10.70", mac: "AA:BB:CC:DD:EE:05", device: "axis-cam-01", os: null, status: "unmanaged", isNew: false,
            deviceType: "Camera", category: "Likely: Camera", scanId: 10, firstSeen: daysAgo(20), lastSeen: daysAgo(2),
            services: [svc(80, "tcp", "Boa HTTPd"), svc(554, "tcp", "RTSP")]},
        ...bulkDiscoveredDevices                                              // 1,200 synthetic scanId-6 rows — demo for the 1000+ infinite-scroll table.
    ].map(d => {
        // Don't relabel a port another scan already identified by name (e.g. the bare + vsFTPd duplicate on device 205).
        const named = new Set((d.services ?? []).filter(s => s.service).map(s => `${s.protocol}/${s.port}`));
        const services = (d.services ?? []).map(s =>
            s.service || named.has(`${s.protocol}/${s.port}`) || !wellKnownPorts[s.port as keyof typeof wellKnownPorts] ? s : {...s, service: wellKnownPorts[s.port as keyof typeof wellKnownPorts]});
        // The backend now returns status as an array; a device can be both "new" and "unmanaged" at once.
        const status = d.status === "managed" ? ["managed"] : d.isNew ? ["new", "unmanaged"] : ["unmanaged"];
        return {...d, status, services, firstScanId: d.isNew ? d.scanId : 1, lastScanId: d.scanId};
    });
    const scannerName = (id?: number) => scanners.find(s => s.deviceId === id)?.device;
    const scanMeta = (scan?: {targetId?: number, state?: string, deviceCount?: number, scannerId?: number, name?: string}) => {
        if (!scan) return undefined;
        const t = scan.targetId == null ? undefined : find(scan.targetId);
        return {...scan, state: scan.state || "completed", liveHosts: scan.deviceCount,
            scannerName: scannerName(scan.scannerId), range: t?.addresses?.[0], name: t?.name ?? scan.name};
    };
    // The Discovery/Activity read model mints its own scan ids (targetOverview, history) that don't exist in this
    // in-memory model — so resolve by exact scanId, then the target's latest scan, then the newest scan, so the
    // discovered-devices table stays populated however the user reached the page.
    const resolveScan = (scanId?: number, targetId?: number) =>
        (scanId && scans.find(s => s.scanId === scanId))
        || (targetId && scans.find(s => s.targetId === targetId))
        || scans[0];
    const filterDevices = (data: DeviceRow[], {statuses, search, deviceType, ip, device, deviceId, service, port, protocol}: DeviceQuery = {}) => {
        let all = [...data];
        // Empty statuses means all statuses (incl. managed); otherwise OR the requested buckets — matches the backend.
        if (statuses?.length)
            all = all.filter(d => statuses.some((s: string) => d.status?.includes(s)));
        if (deviceType) all = all.filter(d => d.deviceType === deviceType);
        if (ip?.trim()) {
            const q = ip.trim().toLowerCase();
            all = all.filter(d => d.ip?.toLowerCase().includes(q));
        }
        if (deviceId != null) all = all.filter(d => String(d.deviceId) === String(deviceId));
        else if (device?.trim()) {
            const q = device.trim().toLowerCase();
            all = all.filter(d => d.device?.toLowerCase().includes(q));
        }
        if (service?.trim()) {
            const q = service.trim().toLowerCase();
            all = all.filter(d => d.services?.some((s: Service) => s.service?.toLowerCase().includes(q)));
        }
        if (port?.trim()) {
            const q = port.trim();
            all = all.filter(d => d.services?.some((s: Service) => String(s.port) === q));
        }
        if (protocol?.trim()) {
            const q = protocol.trim().toLowerCase();
            all = all.filter(d => d.services?.some((s: Service) => s.protocol?.toLowerCase() === q));
        }
        if (search?.trim()) {
            const q = search.trim().toLowerCase();
            all = all.filter(d => [d.ip, d.device, d.mac, d.category, d.os, d.deviceType, ...svcNames(d.services)].some(v => v && String(v).toLowerCase().includes(q)));
        }
        return {data: all, totalInScan: data.length};
    };
    const find = (id: number) => targets.find(t => t.targetId === id);
    // Editable fields, normalized from the request params (shared by create/edit).
    const normalize = ({name, description, addresses, scannerIds, exclusions}: TargetInput) => ({
        name: (name || "").trim(), description: description || "",
        addresses: [...(addresses || [])], exclusions: [...(exclusions || [])], scannerIds: [...(scannerIds || [])]
    });
    return {
        overview: () => {
            return {
                summary: {scanTargets: targets.length},
                targets: targets.map(t => ({...t})),
                scanners: scanners.map(s => ({...s})),
                upcoming: upcoming.map(u => ({...u, targets: u.targets.map((t: HistoryTarget) => ({...t}))})),
            };
        },
        scanners: () => ({data: scanners.map(s => ({deviceId: s.deviceId, device: s.device, ip: s.ip, online: s.online}))}),
        // New Scan dialog target picker: bare array, scanner device names resolved from scannerIds (no scannerIds/devices exposed).
        targetsList: () => ({
            data: targets.map(t => ({
                targetId: t.targetId, name: t.name, description: t.description,
                addresses: [...t.addresses], exclusions: [...(t.exclusions ?? [])],
                scanners: (t.scannerIds ?? []).map(id => scannerName(id)).filter(Boolean),
                lastScan: t.lastScan ?? null,
            })),
            rowCount: targets.length,
        }),
        createTarget: (p: TargetInput) => {
            const targetId = nextId++;
            targets.push({targetId, ...normalize(p), lastScan: null, devices: null});
            return {targetId};
        },
        editTarget: (p: TargetInput) => {
            const t = p.targetId == null ? undefined : find(p.targetId);
            if (t) Object.assign(t, normalize(p));
            return {};
        },
        deleteTarget: ({targetId}: {targetId?: number}) => {
            targets = targets.filter(t => t.targetId !== targetId);
            return {};
        },
        startScan: (p: ScanInput) => {
            const ids = p.targetIds ?? [];
            const ts = ids.map(find).filter((t): t is Target => !!t);
            const scanId = nextScanId++;
            const scanType = p.scanType || "discovery";
            const now = new Date().toISOString();
            ts.forEach((t: Target) => {
                t.lastScan = now;
                t.devices = 5 + (Math.random() * 40 | 0);
                scans.unshift({scanId, targetId: t.targetId, name: t.name, scanType, startedAt: now, deviceCount: t.devices, newCount: 2 + (Math.random() * 4 | 0), scannerId: t.scannerIds?.[0], state: "completed"});
            });
            // Discovery cards read from a separate model — queue the scanned targets there so their card shows "Pending" and stays consistent across navigations.
            discoverySubnetsBase
                .filter(s => ids.includes(s.targetId))
                .forEach(s => Object.assign(s, {status: "pending", scanType}));
            return {};
        },
        // Edit a scheduled scan in place: retarget (targets follow the new selection) and schedule.
        editScan: (p: ScanInput) => {
            const s = upcoming.find(u => u.scanId === p.scanId);
            if (!s) return {ok: false};
            if (p.name != null) s.name = p.name;
            const targetIds: number[] = p.targetIds ?? [];
            if (targetIds.length)
                s.targets = targetIds.map(id => {
                    const t = find(id);
                    return t ? {targetId: t.targetId, name: t.name, addresses: [...t.addresses]} : {targetId: id, name: "", addresses: []};
                });
            if (p.scanType) s.scanType = p.scanType;
            if (p.applyScheme) {
                s.applyScheme = p.applyScheme;
                s.afterTime = p.afterTime;
                s.timezone = p.timezone;
                s.weekDay = p.weekDay;
                s.window = p.window;
            }
            return {ok: true, scanId: p.scanId};
        },
        deactivateScan: ({scanId}: ScanRef) => {
            const s = upcoming.find(u => u.scanId === scanId);
            if (s) {
                s.deactivatedAt = new Date().toISOString();
                s.deactivatedBy = {user: "george@acme.com", userName: "George Mann", title: null};
            }
            return {ok: true};
        },
        activateScan: ({scanId}: ScanRef) => {
            const s = upcoming.find(u => u.scanId === scanId);
            if (s) {
                delete s.deactivatedAt;
                delete s.deactivatedBy;
            }
            return {ok: true};
        },
        deleteScan: ({scanId}: ScanRef) => {
            upcoming = upcoming.filter(u => u.scanId !== scanId);
            return {ok: true};
        },
        scanResults: ({scanId, targetId, statuses, search, deviceType, ip, device, deviceId, service, port, protocol, from = 0, len}: any = {}) => {
            const scan = resolveScan(scanId, targetId);
            const pool = discoveredDevices.filter(d => d.scanId === scan?.scanId);
            const {data, totalInScan} = filterDevices(pool, {statuses, search, deviceType, ip, device, deviceId, service, port, protocol});
            return {data: data.slice(from, len == null ? undefined : from + len), rowCount: data.length, totalInScan, scan: scanMeta(scan)};
        },
        combiField: ({field, scanId, targetId, statuses, deviceType, ip, device, deviceId, service, port, protocol, from = 0, len = 100}: any = {}) => {
            const combiFieldVals = (d: DeviceRow, f: string) => {
                if (f === "device") {
                    const name = d.device && d.device !== "unknown" ? d.device : null;
                    return name ? [name] : [];
                }
                if (f === "service") return svcNames(d.services);
                if (f === "port") return [...new Set((d.services ?? []).map((s: Service) => String(s.port)))];
                if (f === "protocol") return [...new Set((d.services ?? []).map((s: Service) => s.protocol))];
                const v = d[f];
                return v != null && v !== "" && v !== "unknown" ? [String(v)] : [];
            };
            const scan = resolveScan(scanId, targetId);
            const pool = discoveredDevices.filter(d => d.scanId === scan?.scanId);
            const filters: DeviceQuery = {statuses, deviceType, ip, device, deviceId, service, port, protocol};
            // The field being listed must not filter itself, or the list collapses to what is already selected.
            const key = field as keyof DeviceQuery;
            const searchVal = filters[key];
            delete filters[key];
            if (field === "device") delete filters.deviceId;
            const {data} = filterDevices(pool, filters);
            const vals = [...new Set(data.flatMap(d => combiFieldVals(d, field)))].sort((a, b) => a.localeCompare(b, undefined, {numeric: true}));
            const search = typeof searchVal === 'string' ? searchVal.trim() : '';
            const filtered = search
                ? vals.filter(v => v.toLowerCase().includes(search.toLowerCase()))
                : vals;
            const paged = filtered.slice(from, from + len);
            return {
                data: paged, rowCount: filtered.length,
                ...field === "device" && {deviceIds: paged.map(name => data.find(d => d.device === name)?.deviceId)},
            };
        },
        unmanagedDevice: ({deviceId, scanId, MAC, IP}: DeviceQuery) => {
            // Every scanned device resolves (a real backend serves any discovered device); exposure is derived from its services array.
            // Resolve by deviceId, then fall back to MAC / IP within the given scan (all optional context the caller may pass).
            const d = discoveredDevices.find(x => String(x.deviceId) === String(deviceId))
                ?? (MAC && discoveredDevices.find(x => x.mac && x.mac.toLowerCase() === String(MAC).toLowerCase()))
                ?? (IP && discoveredDevices.find(x => x.ip === IP))
                ?? undefined;
            if (!d) return null;
            const scan = scans.find(s => s.scanId === (scanId ?? d.scanId));
            return {
                deviceId: d.deviceId, ip: d.ip, mac: d.mac, device: d.device, category: d.category,
                firstDiscovered: d.firstSeen, lastSeen: d.lastSeen,
                discoveredBy: scannerName(scan?.scannerId), subnet: subnetOf(d.ip), scanType: scan?.scanType ?? "discovery",
                exposure: svcExposure(d.services),
            };
        },
        removeScanner: ({deviceId}: {deviceId?: number}) => {
            scanners = scanners.filter(s => s.deviceId !== deviceId);
            return {ok: true};
        },
        // All scanner-candidate devices, paged (the deploy picker's main query). Deployed ones stay in the list
        // flagged `isScanner`; `ip` is internal bookkeeping and is stripped from each returned row.
        deployableDevices: ({from = 0, len}: any = {}) => {
            const deployed = new Set(scanners.map(s => s.deviceId));
            const data = deployablePool.map(({ip: _ip, ...d}) => ({...d, isScanner: deployed.has(d.deviceId)}));
            return {data: data.slice(from, len == null ? undefined : from + len), rowCount: data.length};
        },
        deployScanner: ({devices}: {devices?: number[]}) => {
            const picked = deployablePool.filter(d => devices?.includes(d.deviceId));
            let deployed = 0;
            picked.forEach(d => {
                if (scanners.some(s => s.deviceId === d.deviceId)) return;
                scanners.push({device: d.device, deviceId: d.deviceId, ip: d.ip, subnet: subnetOf(d.ip), lastScan: null, online: true});
                deployed++;
            });
            return {ok: true, deployed};
        }
    };
})();

export const netscanMock = {
    // --- Discovery / Activity read model ---
    // One card per scanned subnet (Attack Surface · Discovery overview). Wrapped in `data` per the API contract.
    // lastScanId = the target's newest run (from buildTargetScans), so opening the card lands on that scan.
    discoveryOverview: (): {data: DiscoverySubnet[]} => ({data: discoverySubnetsBase
        // Temporary: only surface targets worth showing — completed ("success") scans that actually found devices,
        // plus in-progress (running) and queued (pending) scans. Empty completed scans and never-scanned targets
        // are filtered out here but kept in discoverySubnetsBase, so we can bring them back later.
        .filter(s => s.status === "running" || s.status === "pending"
            || (s.status === "success" && !!((s.devices ?? 0) || (s.new ?? 0) || (s.unmanaged ?? 0))))
        .map(s => {
            const latestScan = buildTargetScans({...s, lastScanId: null})[0];
            return {...s, lastScanId: latestScan?.scanId ?? null, runId: latestScan?.runs[0]?.runId};
        })}),

    // Target view header + KPI summary for the selected scan, plus the scan-history dropdown list.
    // Only the selected scan carries `runs` — scans[] history entries don't (per schema).
    targetOverview: (p: any) => {
        const subnets = netscanMock.discoveryOverview().data;
        const subnet = subnets.find(s => s.targetId === Number(p?.targetId)) ?? subnets[0];
        // create.go always assigns a name — the latest scan got a real one at creation, older ones fall back to "Scan #<id>".
        const scanName = (s: TargetScan, i: number) => i === 0 ? `${subnet.name} sweep` : `Scan #${s.scanId}`;
        const scans = buildTargetScans(subnet);
        const selected = scans.find(s => s.scanId === Number(p?.scanId)) ?? scans[0];
        return {
            target: {targetId: subnet.targetId, name: subnet.name, addresses: subnet.addresses, ips: subnet.ips},
            scan: !selected ? null : {
                scanId: selected.scanId, name: scanName(selected, scans.indexOf(selected)),
                scannedAt: selected.scannedAt, scanType: selected.scanType, status: selected.status,
                liveHosts: selected.devices, unmanaged: selected.unmanaged, newThisScan: selected.new, runs: selected.runs,
                // Illustrative schedule for a recurring scan — real semantics/UI display TBD, not yet consumed anywhere.
                ...(selected.scanId % 2 === 0 && {applyScheme: "afterTime", afterTime: 480, timezone: 0, window: 240}),
            },
            // create.go always assigns a name (user-given, or "Scan #<id>" fallback when none was given) — every
            // scan has one, most via the fallback.
            scans: scans.map((s, i) => ({
                scanId: s.scanId, name: scanName(s, i),
                scannedAt: s.scannedAt, scanType: s.scanType, status: s.status,
                devices: s.devices, new: s.new, latest: i === 0
            })),
        };
    },

    history: (p: any) => {
        let rows = scanHistory;
        if (p?.targetId) rows = rows.filter(s => s.targets.some(t => t.targetId === Number(p.targetId)));
        if (p?.scanId) rows = rows.filter(s => s.scanId === Number(p.scanId));
        if (p?.user) rows = rows.filter(s => s.user === p.user);
        if (p?.scanType) rows = rows.filter(s => s.scanType === p.scanType);
        // lastScanFrom/lastScanTo arrive as Date instances (serialized to RFC3339 for the real backend) — compare by day.
        const day = (d: Date | string) => new Date(d).toISOString().slice(0, 10);
        if (p?.lastScanFrom) rows = rows.filter(s => s.scanTime.slice(0, 10) >= day(p.lastScanFrom));
        if (p?.lastScanTo) rows = rows.filter(s => s.scanTime.slice(0, 10) <= day(p.lastScanTo));
        if (p?.search?.trim()) {
            const q = String(p.search).toLowerCase();
            rows = rows.filter(s => s.targets.some(t => t.name.toLowerCase().includes(q) || t.addresses.some(a => a.includes(q))));
        }
        return page(sortHistory(rows, p?.sort), p);
    },

    // Activity (netscan_history) filter options served by netscan_combi-field: one option list per field.
    // Most fields return {k, h} options; targetId and scanId return the backend's {id, value} shape (mapped to {k, h} in the UI).
    historyCombiField: (p: any) => {
        const opts = ({
            targetId: netscanHistoryTargetOpts,
            scanId: netscanHistoryScanOpts,
            user: netscanHistoryUserOpts,
            scanType: netscanHistoryTypeOpts,
        } as Record<string, {k?: string | number; h?: string; id?: number; value?: string}[]>)[p?.field] ?? [];
        return page(opts, p);
    },

    // --- Stateful demo model (create/edit/delete/scan/deploy persist for the session) ---
    ...netscanState,

    // TargetView's discovered-devices table and the device drilldown are served from the discovery read model
    // (above), not netscanState — so a card click opens a target whose devices, statuses and addresses match
    // its header KPIs. These override the netscanState versions spread just above.
    scanResults: scanResultsA,
    unmanagedDevice: unmanagedDeviceA,
};
